import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, SlidersHorizontal, PauseCircle, FlaskConical, Zap, Clock, TrendingUp, ShieldCheck, Download, Trash2, LogOut, X, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiFetch } from "@/lib/api";
import { Button, Chip, useToast } from "@/components/earnos/kit";
import { colors } from "@/lib/theme";

const GOALS = ["$500/month", "$1,000/month", "$3,000/month", "$5,000/month", "$10,000+/month"];
const DEADLINES = ["30 days", "60 days", "90 days", "6 months", "Long-term growth"];
const TIMES = ["Less than 5 hours/week", "5–10 hours/week", "10–20 hours/week", "20–40 hours/week", "Full-time focus"];

function Toggle({ checked, onChange, disabled, testID, label }) {
  return (
    <button type="button" role="switch" aria-checked={checked} aria-label={label} disabled={disabled} data-testid={testID} onClick={() => !disabled && onChange(!checked)} className={`w-12 h-7 rounded-full transition-colors relative shrink-0 ${checked ? "bg-brand" : "bg-surface-3"} ${disabled ? "opacity-50" : ""}`}>
      <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-white transition-all ${checked ? "left-[22px]" : "left-0.5"}`} />
    </button>
  );
}

function Row({ icon: Icon, tint, label, value, onClick, right, testID }) {
  const RowTag = onClick ? "button" : "div";
  return (
    <RowTag type={onClick ? "button" : undefined} onClick={onClick} data-testid={testID} className={`w-full text-left flex items-center gap-3 rounded-xl border border-line bg-surface-2 p-3 mb-2 ${onClick ? "cursor-pointer hover:border-line-strong" : ""} transition-colors`}>
      <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: tint + "22" }}><Icon size={18} style={{ color: tint }} /></div>
      <div className="flex-1 min-w-0"><p className="text-base font-semibold text-ink">{label}</p>{value ? <p className="text-sm text-ink-2">{value}</p> : null}</div>
      {right}
    </RowTag>
  );
}

export default function Settings() {
  const navigate = useNavigate();
  const toast = useToast();
  const { user, logout, refresh } = useAuth();
  const [prefs, setPrefs] = useState(null);
  const [assessmentReady, setAssessmentReady] = useState(false);
  const engineActionLock = useRef(false);
  const [prefsSaving, setPrefsSaving] = useState(false);
  const prefsSaveLock = useRef(false);
  const [settingsError, setSettingsError] = useState("");
  const [engineModal, setEngineModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [goal, setGoal] = useState("");
  const [deadline, setDeadline] = useState("");
  const [time, setTime] = useState("");
  const [demoBusy, setDemoBusy] = useState(false);

  const loadSettings = useCallback(async () => {
    setSettingsError(""); setAssessmentReady(false);
    const results = await Promise.allSettled([
      apiFetch("/notifications/prefs"), apiFetch("/assessment"),
    ]);
    if (results[0].status === "fulfilled") setPrefs(results[0].value);
    if (results[1].status === "fulfilled") {
      setAssessmentReady(true);
      const assessment = results[1].value?.assessment;
      if (assessment) {
        setGoal(assessment.income_goal || "");
        setDeadline(assessment.deadline || "");
        setTime(assessment.time_per_week || "");
      }
    }
    const failed = results.filter(result => result.status === "rejected");
    if (failed.length) setSettingsError("Some settings could not be loaded. Please retry before changing them.");
  }, []);
  useEffect(() => { loadSettings(); }, [loadSettings]);

  const togglePref = async (key) => {
    if (!prefs || prefsSaveLock.current) return;
    prefsSaveLock.current = true;
    setPrefsSaving(true);
    const next = { ...prefs, [key]: !prefs[key] };
    try {
      await apiFetch("/notifications/prefs", { method: "PUT", body: next });
      setPrefs(next);
      toast.show("Notification preference saved", "success");
    } catch (e) {
      toast.show(e?.message || "Could not save notification preference. Please try again.", "error");
    } finally {
      prefsSaveLock.current = false;
      setPrefsSaving(false);
    }
  };

  const toggleDemo = async () => {
    setDemoBusy(true);
    try {
      if (user?.demo_mode) await apiFetch("/demo/disable", { method: "POST" });
      else await apiFetch("/demo/enable", { method: "POST" });
      await refresh();
      toast.show(user?.demo_mode ? "Demo data cleared" : "Demo data loaded", "success");
    } catch (e) { toast.show(e?.message || "Failed", "error"); }
    finally { setDemoBusy(false); }
  };

  const regenerate = async () => {
    if (!assessmentReady || engineActionLock.current) return;
    engineActionLock.current = true;
    setRegenerating(true);
    try {
      await apiFetch("/engine/settings", { method: "PATCH", body: { income_goal: goal, deadline, time_per_week: time } });
      await apiFetch("/engine/generate", { method: "POST" });
      toast.show("Your Income Engine was updated", "success");
      setEngineModal(false);
      navigate("/engine");
    } catch (e) { toast.show(e?.message || "Could not update", "error"); }
    finally { engineActionLock.current = false; setRegenerating(false); }
  };

  const pauseEngine = async () => {
    if (engineActionLock.current) return;
    engineActionLock.current = true;
    try { await apiFetch("/engine/pause", { method: "POST" }); toast.show("Income Engine paused", "info"); }
    catch (e) { toast.show(e.message || "Could not pause the engine. Please retry.", "error"); }
    finally { engineActionLock.current = false; }
  };

  const exportData = async () => {
    try {
      const data = await apiFetch("/account/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `earnos-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.show("Your data file has downloaded", "success");
    } catch (e) { toast.show(e?.message || "Failed", "error"); }
  };

  const deleteAccount = async () => {
    try {
      await apiFetch("/account", { method: "DELETE" });
      setDeleteModal(false);
      await logout();
      navigate("/auth", { replace: true });
    } catch (e) { toast.show(e?.message || "Failed", "error"); }
  };

  const doLogout = async () => { await logout(); navigate("/auth", { replace: true }); };

  return (
    <div className="animate-fade-in max-w-2xl">
      <div className="flex items-center gap-3 mb-2">
        <button onClick={() => navigate(-1)} data-testid="settings-back" className="text-ink lg:hidden"><ChevronLeft size={26} /></button>
        <h1 className="text-2xl font-extrabold tracking-tight text-ink">Settings</h1>
      </div>

      {settingsError && <div role="alert" className="text-sm text-danger mt-3">
        {settingsError} <button type="button" onClick={loadSettings} className="underline font-semibold">Retry</button>
      </div>}
      <p className="earnos-kicker mt-6 mb-3">Income Engine</p>
      <Row icon={SlidersHorizontal} tint={colors.brand} label="Change goal & pace" value={goal || "Set your goal"} onClick={() => assessmentReady ? setEngineModal(true) : toast.show("Load your settings successfully before changing your goal.", "error")} testID="change-goal" />
      <Row icon={PauseCircle} tint={colors.warning} label="Pause Income Engine" onClick={pauseEngine} testID="pause-engine" />

      <p className="earnos-kicker mt-6 mb-3">Demo</p>
      <Row icon={FlaskConical} tint={colors.brandTertiary} label="Demo mode" value="Loads sample 'Jordan' data — clearly labeled" right={<Toggle label="Demo mode" checked={!!user?.demo_mode} onChange={toggleDemo} disabled={demoBusy} testID="demo-switch" />} />

      {prefs && (
        <>
          <p className="earnos-kicker mt-6 mb-3">Notifications</p>
          <Row icon={Zap} tint={colors.info} label="Today's Money Move" right={<Toggle label="Today's Money Move" disabled={prefsSaving} checked={!!prefs.money_move} onChange={() => togglePref("money_move")} />} />
          <Row icon={Clock} tint={colors.info} label="Follow-up due" right={<Toggle label="Follow-up due" disabled={prefsSaving} checked={!!prefs.follow_up} onChange={() => togglePref("follow_up")} />} />
          <Row icon={TrendingUp} tint={colors.info} label="Goal progress" right={<Toggle label="Goal progress" disabled={prefsSaving} checked={!!prefs.goal_progress} onChange={() => togglePref("goal_progress")} />} />
        </>
      )}

      <p className="earnos-kicker mt-6 mb-3">Security</p>
      <Row icon={ShieldCheck} tint={colors.success} label="Multi-factor authentication" value={user?.mfa_enabled ? "Enabled" : "Available soon"} right={<Toggle label="Multi-factor authentication" checked={!!user?.mfa_enabled} onChange={() => {}} disabled />} />

      <p className="earnos-kicker mt-6 mb-3">Account</p>
      <Row icon={Download} tint={colors.info} label="Export my data" onClick={exportData} testID="export-data" />
      <Row icon={Trash2} tint={colors.error} label="Delete account" onClick={() => setDeleteModal(true)} testID="delete-account" />
      <Row icon={LogOut} tint={colors.onSurfaceSecondary} label="Log out" onClick={doLogout} testID="logout" />

      <p className="text-sm text-ink-2 leading-relaxed mt-8 text-center">EARNOS provides AI-generated recommendations and estimates. It does not guarantee income. Results vary and depend on execution, demand, and other factors.</p>

      {engineModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center" onClick={() => setEngineModal(false)}>
          <div role="dialog" aria-modal="true" aria-label="Change goal and pace" className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl bg-surface-2 border border-line p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2"><h3 className="text-xl font-bold text-ink">Change goal &amp; pace</h3><button type="button" aria-label="Close goal and pace settings" disabled={regenerating} onClick={() => setEngineModal(false)} className="text-ink"><X size={24} /></button></div>
            <p className="text-sm text-warn mb-4">This may change your recommended path and 30-day plan.</p>
            <p className="text-xs font-bold uppercase tracking-wide text-ink-2 mb-2">Income goal</p>
            <div className="flex flex-wrap gap-2">{GOALS.map((g) => <Chip key={g} label={g} selected={goal === g} onClick={() => setGoal(g)} />)}</div>
            <p className="text-xs font-bold uppercase tracking-wide text-ink-2 mb-2 mt-4">Deadline</p>
            <div className="flex flex-wrap gap-2">{DEADLINES.map((d) => <Chip key={d} label={d} selected={deadline === d} onClick={() => setDeadline(d)} />)}</div>
            <p className="text-xs font-bold uppercase tracking-wide text-ink-2 mb-2 mt-4">Time per week</p>
            <div className="flex flex-wrap gap-2">{TIMES.map((t) => <Chip key={t} label={t} selected={time === t} onClick={() => setTime(t)} />)}</div>
            <Button title="Save & Regenerate Engine" onClick={regenerate} loading={regenerating} full className="mt-5" testID="regenerate-engine" />
          </div>
        </div>
      )}

      {deleteModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-6" onClick={() => setDeleteModal(false)}>
          <div role="dialog" aria-modal="true" aria-label="Delete account" className="w-full max-w-md rounded-2xl bg-surface-2 border border-line p-6 flex flex-col items-center text-center" onClick={(e) => e.stopPropagation()}>
            <AlertTriangle size={32} className="text-danger" />
            <h3 className="text-xl font-bold text-ink mt-3">Delete your account?</h3>
            <p className="text-base text-ink-2 mt-2 leading-relaxed">This deletes your account and operational content. Billing and audit records may be retained for payment, disputes, security, and legal obligations. Cancel an active subscription first. This cannot be undone.</p>
            <div className="flex gap-3 mt-5 w-full">
              <Button title="Cancel" variant="secondary" onClick={() => setDeleteModal(false)} full />
              <Button title="Delete" variant="danger" onClick={deleteAccount} full testID="confirm-delete" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
